import { defineStore } from 'pinia';
import { ref, computed } from 'vue';
import { useCartStore } from '@white-label-store/cart';
import { useCartCheckoutStore } from '@white-label-store/cart-checkout';
import { useSearchResultsStore } from '@white-label-store/search-results';
import { EXTRAS_PRODUCT_CODES, ROUTE_NAMES } from '@white-label-configuration/constants';

export interface Traveller {
  id: string;
  dateOfBirth: Date | string;
  countryOfResidence?: { name: string; id: string };
  isPrimary: boolean;
}

export type TravelInsuranceForm = {
  destination: string[];
  travelDates: {
    startDate: Date | string;
    endDate: Date | string;
  };
  travellers: Traveller[];
};

interface InsuranceOption {
  id: number;
  name: string;
  price: string;
  currency: string;
  description: string;
  features: string[];
}
interface QuoteResponse {
  status: 'success' | 'error';
  quoteId: string;
  options: InsuranceOption[];
  policy: object;
  total: number;
}

export const useTravelInsuranceStore = defineStore('travelInsurance', () => {
  const form = ref<TravelInsuranceForm>({
    destination: [],
    travelDates: {
      startDate: '',
      endDate: '',
    },
    travellers: [
      {
        id: '1',
        dateOfBirth: '',
        countryOfResidence: { name: '', id: '' },
        isPrimary: true
      }
    ],
  });

  const activeStep = ref(0);
  const quoteResults = ref<QuoteResponse | null>(null);
  const cartId = ref<string | null>(null);
  const selectedAddOnIds = ref<string[]>([]);
  const searchToken = ref<string | null>(null);

  // check for travel insurance product in results
  const hasTravelInsuranceInResults = computed(() => {
    const searchResultsStore = useSearchResultsStore();
    const travelInsurance = searchResultsStore.searchResultsByProductType?.['travel_insurance'];
    return Array.isArray(travelInsurance) && travelInsurance.length > 0;
  });

  // check for travel insurance in cart
  const travelInsuranceCartItem = computed(() => {
    const cartStore = useCartStore();
    const cartCheckoutStore = useCartCheckoutStore();
    const cartItems = cartStore.readCartItems;
    const checkoutCartItems = cartCheckoutStore.readCartItems;

    const items = cartItems.length ? cartItems : checkoutCartItems;

    if (!items || items.length === 0) {
      return null;
    }

    return items.find(item =>
      item.product_code === EXTRAS_PRODUCT_CODES.TRAVEL_INSURANCE ||
      item.product_code === 'travel-insurance' ||
      item.product_type === 'travel-insurance'
    ) || null;
  });

  const hasTravelInsuranceInCart = computed(() => {
    return travelInsuranceCartItem.value !== null;
  });

  const quoteStarted = computed(() => {
    const step1HasData = form.value.destination.length > 0;
    const step2HasData = form.value.travelDates?.startDate !== '' && form.value.travelDates?.endDate !== '';
    const step3HasData = form.value.travellers[0].countryOfResidence?.id !== '' && form.value.travellers[0].dateOfBirth !== '';
    return step1HasData || step2HasData || step3HasData;
  });

  // check if disclosure message should show up
  function shouldShowDisclosure(currentRouteName?: string): boolean {
    const isOnTravelExtrasPage = currentRouteName === ROUTE_NAMES.travel_extras;

    // show disclosure when either on travel-extras page and travel insurance is in results
    // or travel insurance is in cart - for payment page etc
    return (isOnTravelExtrasPage && hasTravelInsuranceInResults.value) || hasTravelInsuranceInCart.value;
  }

  function setQuoteResults(payload: QuoteResponse) {
    quoteResults.value = payload;
  }

  function setSearchToken(token: string) {
    searchToken.value = token;
  }

  function updateFormField<K extends keyof TravelInsuranceForm>(key: K, value: TravelInsuranceForm[K]) {
    form.value[key] = value;
  }

  // Form data is persisted so that data is not lost if the page is refreshed
  // However, if the cart id changes (i.e user starts a new order) then we need to clear the form
  function initForm() {
    const currentCartId = useCartStore().readToken;
    if (cartId.value && cartId.value !== currentCartId) {
      clearForm();
    }
    cartId.value = currentCartId;
  }

  function clearForm() {
    form.value = {
      destination: [],
      travelDates: {
        startDate: '',
        endDate: '',
      },
      travellers: [
        {
          id: '1',
          dateOfBirth: '',
          countryOfResidence: '',
          isPrimary: true
        }
      ],
    };
    activeStep.value = 0;
    quoteResults.value = null;
    searchToken.value = null;
    cartId.value = null;
  }

  return {
    activeStep,
    cartId,
    clearForm,
    form,
    hasTravelInsuranceInResults,
    hasTravelInsuranceInCart,
    initForm,
    selectedAddOnIds,
    setQuoteResults,
    setSearchToken,
    shouldShowDisclosure,
    travelInsuranceCartItem,
    quoteResults,
    searchToken,
    quoteStarted,
    updateFormField,
  };
}, {
  persist: true,
});
